import { CartItemAdditionTypes, EventSources } from '../constants/event';
import { ModalityType } from '../generated-interfaces/graphql';
import { CartItem } from '../utils/cart';
import { ModSymbolCodeNameMappingType } from '../utils/mappings';
import { PosProperties } from '../generated-interfaces/graphql';
import { GenericMap } from '../utils/types';
import { EntityMenuItem, TransmissionMessage } from './messagingSlice';
import { LOYALTY_APPLY_STATUS } from './cartSlice.constants';
import { ISendItemProperties } from './orderSlice.props';

export interface ISendLoyaltyData {
  check_id: string;
  store_id: string;
  request_id: string;
  session_id: string;
  source: EventSources;
  loyalty_code: string;
  seq_id: string;
}

export interface ILoyaltyTransmissionMessage extends TransmissionMessage {
  data: ISendLoyaltyData;
}

export type LoyaltyCouponItem = {
  name: string;
  couponno: string;
  modifiers: string[];
  isApplied?: boolean;
};

type LoyaltyRequest = {
  id: string;
  status: string;
  transactionId: string;
  loyaltyResponse: any;
  couponResponse: any;
};

export type Coupon = {
  id: string;
  code: string;
};

export interface IEntityCartItem extends EntityMenuItem {
  cartItemId: number;
}

export interface ICartLoyalty {
  code: string;
  addedBy?: CartItemAdditionTypes;
}

export interface ISelectModifierPayload {
  cartItemId: number;
  menuItemId: string;
  modGroupId: string;
  selected: boolean;
  modCode: string;
  modSymbolMapping: ModSymbolCodeNameMappingType;
  pathToItem?: string;
  rootModGroupId?: string;
  posSettings: GenericMap<PosProperties>;
  readback?: boolean;
  quantity?: number;
  bmItemsSynonyms?: Record<string, Array<string>>;
  applySuppressCascade?: boolean;
  suppressUpsellEnabled?: boolean;
  applyExpressCascade?: boolean;
  // This session's withheld constraints, declined in the inserted subtree.
  withheldConstraints?: string[];
}

export interface IBatchedModifierSelection {
  selection: ISelectModifierPayload;
  // When present, the selection (and its whole subtree) is dropped if the
  // modifier is an unavailable brand-menu constraint at apply time. The check
  // must run against the cart as previous selections in the batch have
  // already been applied, which is why it lives in the reducer.
  unavailableConstraintCheck?: {
    parentModifier: string;
    modGroupName: string;
    itemName: string;
  };
  children?: IBatchedModifierSelection[];
}

/**
 * HITL-1611 — the cart's item core, and nothing else.
 *
 * Exactly the five fields the item reducers and their helpers touch:
 * `applyModifierSelection` destructures only `{ cartItems, modality }`, the two
 * collapse helpers touch only `cartItemCollapseStates`, and the eight reducers
 * in `cartSlice.itemReducers.ts` add `cartItemsQuantity` and `sequenceId`.
 *
 * It exists so `draftCart` can reuse those reducers verbatim without carrying
 * the cart's session machinery — loyalties, coupons, POS cart, requests — none
 * of which the agent's draft has any business holding. `ICartState` extends it,
 * so widening the reducers to this type changes no behaviour in `cartSlice`.
 */
export interface ICartItemsState {
  cartItems: GenericMap<CartItem>;
  sequenceId: number;
  modality: ModalityType;
  cartItemsQuantity: Record<string, number>;
  cartItemCollapseStates: Record<number, number>;
}

export interface ICartState extends ICartItemsState {
  loyaltyCouponItem?: LoyaltyCouponItem;
  currentTransactionId: string | null;
  currentSessionId: string | null;
  requests: {
    [id: string]: LoyaltyRequest;
  };
  coupons: GenericMap<Coupon>;
  loyaltyCode?: string;
  loyaltyApplyStatus?: LOYALTY_APPLY_STATUS;
  hypothesisCartItems: GenericMap<IEntityCartItem>;
  posCartItems: ISendItemProperties[];
  loyalties: ICartLoyalty[];
}

export const initialCartState: ICartState = {
  cartItems: {},
  sequenceId: 1,
  modality: ModalityType.Dinein,
  cartItemsQuantity: {},
  currentTransactionId: null,
  currentSessionId: null,
  requests: {},
  coupons: {},
  hypothesisCartItems: {},
  posCartItems: [],
  loyalties: [],
  cartItemCollapseStates: {},
};
